/**
 * `?email=`, an address we already collected, so sign-up starts one field shorter.
 *
 * 🔴 IT WAS BEING PASSED AND SILENTLY IGNORED. The public Labs preview has captured an address by the
 * time it hands a reader here, and `signUpHref` has always appended it. Both sign-up forms then threw
 * it away: the control hardcodes `emailAddress: ''` and the experiment seeds from its own draft store.
 * So the reader typed their address, watched it vanish, and typed it again. Found by Jay testing the
 * flow end to end, 2026-08-28.
 *
 * INERT FOR ORDINARY SIGN-UP TRAFFIC. Nothing else in the product links to `/sign-up?email=`, so on
 * every normal arrival this returns '' and both forms behave exactly as they do today. That matters
 * because sign-up carries a live A/B test (`ab_test_sign_up_2026_q1`) and a prefill must not become a
 * silent third condition in it.
 *
 * VALIDATED, NOT TRUSTED. The value is untrusted query input that lands in a form field. A junk value
 * would populate the field with something that fails validation the moment they submit, which looks
 * like our bug rather than a bad link, so anything that is not plausibly an address is dropped.
 */

/** Deliberately permissive: this only decides whether to PREFILL. Zod still validates on submit. */
const PLAUSIBLE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Longest address we will prefill. Bounded because it is untrusted input rendered into an input. */
const MAX_EMAIL_LENGTH = 254;

/** The query parameter carrying a known address. Written by `signUpHref` in the Labs preview payoff. */
export const PREFILLED_EMAIL_PARAM = 'email';

/**
 * Read a prefillable address from the URL, or '' when there is not a usable one.
 *
 * Returns a string rather than null so it can drop straight into `defaultValues` without a fallback at
 * every call site, which is how one of the two forms would eventually get missed again.
 */
export function prefilledEmail(searchParams: Pick<URLSearchParams, 'get'> | null): string {
  const raw = searchParams?.get(PREFILLED_EMAIL_PARAM)?.trim() ?? '';

  if (raw.length === 0 || raw.length > MAX_EMAIL_LENGTH || !PLAUSIBLE_EMAIL_RE.test(raw)) {
    return '';
  }

  return raw;
}

/** The query parameter carrying the reader's marketing choice, made before they got here. */
export const PREFILLED_NEWSLETTER_PARAM = 'newsletter';

/**
 * The marketing preference a reader already expressed, or null when they have not been asked.
 *
 * 🔴 WITHOUT THIS, THE FAST PATH OPTS PEOPLE IN SILENTLY. The sign-up store defaults `newsletter` to
 * true and its checkbox lives on the CHOOSER screen. Skipping straight to the email form for a reader
 * we already know therefore hides the only control over marketing mail while leaving it switched on.
 * That is consent bundling by omission, the same thing the Labs payoff had to stop doing, and it would
 * be worse here because the reader never sees a box at all. Found by Bugbot on #2843.
 *
 * Null when absent, so ordinary sign-up traffic keeps the store's own default untouched.
 */
export function prefilledNewsletter(searchParams: Pick<URLSearchParams, 'get'> | null): boolean | null {
  const raw = searchParams?.get(PREFILLED_NEWSLETTER_PARAM);

  if (raw === '1') {
    return true;
  }
  if (raw === '0') {
    return false;
  }
  return null;
}
